<script setup lang="ts">
import { computed, ref } from "vue";
import { useTranslation } from "i18next-vue";
import { useApplyFieldPartTemplate } from "@/stores/queries/field-observations.query";
import FieldPartTemplatePicker from "@/components/field-part-templates/FieldPartTemplatePicker.vue";
import DRDialog from "@/components/UI/feedbacks/DRDialog.vue";

/**
 * Insertion d'un modèle de parties depuis l'outil dessin : vraie modal centrée
 * aux couleurs du canvas (la feuille du bas de l'outil terrain est faite pour le
 * doigt sur téléphone, elle jure sur une tablette en paysage).
 * La sélection du modèle est mutualisée avec `FieldPartTemplatePicker`.
 */

const props = defineProps<{
  modelValue: boolean;
  diagnosticId: number;
  /** Parent sous lequel greffer l'arborescence ; absent = à la racine. */
  parentPartId?: number;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "inserted"): void;
}>();

const { t } = useTranslation();

const { mutate: applyTemplate, isPending } = useApplyFieldPartTemplate();

const selectedTemplateId = ref<number | null>(null);

const show = computed({
  get: () => props.modelValue,
  set: (value: boolean) => {
    if (!value) selectedTemplateId.value = null;
    emit("update:modelValue", value);
  },
});

const handleInsert = () => {
  if (!selectedTemplateId.value) return;

  applyTemplate(
    {
      diagnosticId: props.diagnosticId,
      templateId: selectedTemplateId.value,
      parentPartId: props.parentPartId,
    },
    {
      onSuccess: () => {
        emit("inserted");
        show.value = false;
      },
    },
  );
};
</script>

<template>
  <DRDialog
    v-model="show"
    class="canvas-dialog template-dialog"
    :title="t('dashboard:fieldObservations.partTemplates.insertModal.title')"
    :confirm-label="
      t('dashboard:fieldObservations.partTemplates.insertModal.insert')
    "
    @confirm="handleInsert"
  >
    <FieldPartTemplatePicker v-model="selectedTemplateId" />

    <div v-if="isPending" class="pending-row">
      <span class="spinner-border spinner-border-sm me-2" />
      {{ t("common:loading") }}
    </div>
  </DRDialog>
</template>

<style lang="scss">
/* Non scopé : le contenu du dialog est téléporté hors du composant.
   Les tons du canvas remplacent les jetons Bootstrap, clairs par défaut. */
.el-dialog.template-dialog {
  .template-option {
    border-color: var(--canvas-border);
    background-color: rgba(255, 255, 255, 0.04);

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        border-color: var(--canvas-btn-active-bg);
      }
    }

    &.selected {
      border-color: var(--canvas-btn-active-bg);
      background-color: rgba(255, 255, 255, 0.08);
    }
  }

  .template-radio {
    border-color: rgba(255, 255, 255, 0.25);
  }

  .selected .template-radio {
    border-color: var(--canvas-btn-active-bg);
  }

  .radio-dot {
    background-color: var(--canvas-btn-active-bg);
  }

  .template-name,
  .tree-preview__node {
    color: rgba(255, 255, 255, 0.95);
  }

  .template-description,
  .picker-state,
  .preview-label,
  .tree-preview__path {
    color: rgba(255, 255, 255, 0.5);
  }

  .preview-section {
    border-color: var(--canvas-border);
  }

  .preview-label {
    background-color: rgba(255, 255, 255, 0.06);
  }

  .pending-row {
    display: flex;
    align-items: center;
    justify-content: center;
    padding-top: 12px;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.6);
  }
}
</style>
